import { Injectable, inject } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import { ApiServiceBase } from './api.service.base';
import { UserStateService } from './user-state.service';

import { logger } from '../../helpers/logger';
export interface User {
  id: string;
  name: string;
  email: string;
  isAdmin: boolean;
  isAuthenticated: boolean;
  isShowcaseUser: boolean;
}

export interface OriginalUserPermissions {
  hasSecurityOverrides: boolean;
  isAdmin: boolean;
  canImpersonate: boolean;
}

// API response interface matching the backend UserModel
interface ApiUserModel {
  id: string;
  userName: string;
  email: string;
  emailConfirmed: boolean;
  isExternalLogin: boolean;
  isAdmin: boolean;
  client_ID: number | null;
  clientName: string;
  globalRoleId: number | null;
  globalRoleName: string | null;
  isInternal: boolean;
  isShowcaseUser: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class UserService extends ApiServiceBase {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  private userStateService = inject(UserStateService);

  getCurrentUser(): Observable<User> {
    // Use the centralized UserStateService
    return this.userStateService.currentUser$.pipe(
      map((user: any) => (user ? this.mapApiUserToUser(user) : null)),
      filter((user: User | null): user is User => user !== null)
    );
  }

  private mapApiUserToUser(apiUser: ApiUserModel): User {
    return {
      id: apiUser.id,
      name: apiUser.userName,
      email: apiUser.email,
      isAdmin: apiUser.isAdmin,
      isAuthenticated: true, // If we get a response, user is authenticated
      isShowcaseUser: apiUser.isShowcaseUser, // Use the backend-calculated value
    };
  }

  isAuthenticated(): boolean {
    const user = this.currentUserSubject.value;
    return user?.isAuthenticated ?? false;
  }

  isAdmin(): boolean {
    const user = this.currentUserSubject.value;
    return user?.isAdmin ?? false;
  }

  getUserName(): string {
    const user = this.currentUserSubject.value;
    return user?.name ?? '';
  }

  setCurrentUser(user: User | null): void {
    this.currentUserSubject.next(user);
  }

  loadCurrentUser(): void {
    this.getCurrentUser().subscribe({
      next: user => this.setCurrentUser(user),
      error: error => {
        // Only log errors that aren't 401 Unauthorized (expected for unauthenticated users)
        if (error.status !== 401) {
          logger.error('Failed to load current user:', error);
        }
        this.setCurrentUser(null);
      },
    });
  }

  getOriginalUserPermissions(): Observable<OriginalUserPermissions> {
    // Use the centralized UserStateService
    return this.userStateService.originalUserPermissions$.pipe(
      map(
        (permissions: any) =>
          permissions || {
            hasSecurityOverrides: false,
            isAdmin: false,
            canImpersonate: false,
          }
      )
    );
  }
}
